import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Save, Loader2, Eye, Printer } from "lucide-react";

interface FormActionBarProps {
  hasChanges: boolean;
  hasValidChanges: boolean;
  isSubmitting: boolean;
  mode: "create" | "edit";
  onSubmit: () => void;
  onCancel: () => void;
  className?: string;
  attachmentsSlot?: React.ReactNode;
  /** Doctor's queue button (Next / End consultation) for the patient being prescribed */
  queueSlot?: React.ReactNode;
  /** The queue button is showing: it is the main action, so Save & print steps back to outline */
  queueMain?: boolean;
  /** What's on the prescription so far, e.g. "3 medicines · follow-up Sat, 04 Oct" */
  summary?: string;
  /** Print preview of the unsaved form */
  onPreview?: () => void;
  /** Save, then open the print preview of the saved prescription; when set it becomes the primary action */
  onSubmitAndPrint?: () => void;
}

export default function FormActionBar({
                                        hasChanges,
                                        hasValidChanges,
                                        isSubmitting,
                                        mode,
                                        onSubmit,
                                        onCancel,
                                        className,
                                        attachmentsSlot,
                                        queueSlot,
                                        queueMain = false,
                                        summary,
                                        onPreview,
                                        onSubmitAndPrint,
                                      }: FormActionBarProps) {
  // Track whether form has been successfully saved at least once
  const [savedOnce, setSavedOnce] = useState(false);

  // When isSubmitting transitions from true → false and no changes remain,
  // a save just completed successfully
  const [wasSubmitting, setWasSubmitting] = useState(false);

  useEffect(() => {
    if (wasSubmitting && !isSubmitting && !hasChanges) {
      setSavedOnce(true);
    }
    setWasSubmitting(isSubmitting);
  }, [isSubmitting, hasChanges]);

  // Reset savedOnce when new changes are made
  useEffect(() => {
    if (hasChanges) setSavedOnce(false);
  }, [hasChanges]);

  const showSaved     = savedOnce && !hasChanges && !isSubmitting;
  const showUnsaved   = hasChanges && !isSubmitting;
  const showSaving    = isSubmitting;

  return (
      <div className={cn(
          "prescription-action-bar shadow-[0_-2px_12px_rgba(0,0,0,0.06)]",
          className
      )}>
        <div className="px-4 py-2 md:py-3.5">
          <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-4 gap-y-2">

            {/* Status indicator — inline, no box */}
            {/* Phones: beside the buttons rather than on a row of its own; gone when there's nothing to say */}
            <div className="flex items-center gap-2 min-w-0 flex-none sm:flex-1 h-9 empty:hidden sm:empty:flex">
              {showSaving && (
                  <div className="flex items-center gap-1.5 text-xs text-primary">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Saving…</span>
                  </div>
              )}
              {showUnsaved && (
                  <div className="flex items-center gap-1.5 text-xs text-warning">
                    <div className="w-1.5 h-1.5 bg-warning rounded-full flex-shrink-0" />
                    {/* Phones: the dot alone — the buttons need the width */}
                    <span className="hidden sm:inline">Unsaved changes</span>
                  </div>
              )}
              {showSaved && (
                  <div className="flex items-center gap-1.5 text-xs text-success">
                    <div className="w-1.5 h-1.5 bg-success rounded-full flex-shrink-0" />
                    <span className="hidden sm:inline">Saved</span>
                  </div>
              )}
              {summary && <span className="hidden sm:inline text-xs text-muted-foreground truncate">{summary}</span>}
            </div>

            {/* The prescription's own actions, then the queue button as the main action */}
            {/* Phones: its own row above the prescription buttons; wider screens: inline, last */}
            {queueSlot && (
                <div className="order-first sm:order-last w-full sm:w-auto flex-shrink-0 pb-2 sm:pb-0 border-b sm:border-b-0 sm:border-l border-border sm:pl-2 empty:hidden">
                  {queueSlot}
                </div>
            )}

            {/* Action buttons */}
            {/* Phones: one row */}
            <div className="flex items-center justify-end gap-1.5 sm:gap-2 flex-1 min-w-0 sm:flex-none sm:flex-shrink-0 max-w-full">
              {attachmentsSlot}

              <Button
                  type="button"
                  variant="ghost"
                  onClick={onCancel}
                  disabled={isSubmitting}
                  className="shrink-0 px-2.5 sm:px-3.5 text-muted-foreground"
              >
                Cancel
              </Button>

              {onPreview && (
                  <Button
                      type="button"
                      variant="outline"
                      onClick={onPreview}
                      disabled={isSubmitting}
                      aria-label="Preview"
                      className="px-3.5 gap-1.5"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Preview</span>
                  </Button>
              )}

              <Button
                  type="submit"
                  variant={onSubmitAndPrint ? "outline" : "default"}
                  onClick={onSubmit}
                  disabled={!hasValidChanges || isSubmitting}
                  className="shrink-0 whitespace-nowrap px-3 sm:px-4 gap-1.5"
              >
                {isSubmitting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                    <>
                      {/* Phones: text only, room for "Save & print" */}
                      <Save className="hidden sm:block w-3.5 h-3.5" />
                      {mode === "edit" ? "Update" : "Save"}{!onSubmitAndPrint && <span className="hidden sm:inline"> Prescription</span>}
                    </>
                )}
              </Button>

              {onSubmitAndPrint && (
                  <Button
                      type="button"
                      variant={queueMain ? "outline" : "default"}
                      onClick={onSubmitAndPrint}
                      disabled={!hasValidChanges || isSubmitting}
                      className="shrink-0 whitespace-nowrap px-3 sm:px-4 gap-1.5"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    {mode === "edit" ? "Update" : "Save"} &amp; print
                  </Button>
              )}
            </div>
          </div>
        </div>
      </div>
  );
}